import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Award, Clock, CheckCircle, Star } from "lucide-react";
import "./CourierPerformancePage.css";
import axios from "axios";
import cacheStorage from "../../../utils/cacheStorage";
const BASE_URL = 'https://smart-cargo-backend-923182991420.us-central1.run.app';


const getAuthHeaders = () => {
  const token = cacheStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CourierPerformancePage() {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCouriers = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/couriers`, {
          headers: getAuthHeaders(),
        });

        const mapped = res.data.map((courier) => ({
          name: courier.name,             // ← adjust to match your API response
          deliveries: courier.deliveries, // ← adjust to match your API response
          rating: courier.rating,         // ← adjust to match your API response
        }));

        setPerformanceData(mapped);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCouriers();
  }, []);

  const topPerformers = [...performanceData].sort((a, b) => b.rating - a.rating || b.deliveries - a.deliveries);

  return (
      <div className="courier-performance-page">
        <div className="page-header">
          <div className="page-title-group">
            <h1>Courier Performance</h1>
            <p>Analyze courier metrics and efficiency</p>
          </div>
        </div>

        <div className="metrics-cards-grid">
          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-title">Avg Deliveries/Day</span>
              <div className="metric-icon blue-bg">
                <TrendingUp size={20} />
              </div>
            </div>
            <div className="metric-value">27</div>
            <div className="metric-subtext positive">↑ 8% this week</div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-title">Top Rated</span>
              <div className="metric-icon blue-bg">
                <Award size={20} />
              </div>
            </div>
            <div className="metric-value">Maria G.</div>
            <div className="metric-subtext">
              <span className="rating-text">4.9 <Star size={12} fill="currentColor" className="star-icon" /> rating</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-title">Avg Response Time</span>
              <div className="metric-icon blue-bg">
                <Clock size={20} />
              </div>
            </div>
            <div className="metric-value">8 min</div>
            <div className="metric-subtext positive">↓ 2 min faster</div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <span className="metric-title">Success Rate</span>
              <div className="metric-icon blue-bg">
                <CheckCircle size={20} />
              </div>
            </div>
            <div className="metric-value">96.2%</div>
            <div className="metric-subtext positive">↑ 1.5%</div>
          </div>
        </div>

        <div className="chart-section">
          <h2>Deliveries by Courier</h2>
          <div className="chart-container">
            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p>Error: {error}</p>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={performanceData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#708094', fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#708094', fontSize: 12 }} dx={-10} />
                    <Tooltip cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="deliveries" fill="#3B5998" radius={[4, 4, 0, 0]} barSize={100} />
                  </BarChart>
                </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bottom-grid">
          <div className="top-performers-section">
            <h2>Top Performers</h2>
            <div className="performers-list">
              {topPerformers.map((performer, index) => (
                  <div className="performer-item" key={performer.name}>
                    <div className="performer-rank">{index + 1}</div>
                    <div className="performer-info">
                      <div className="performer-name">{performer.name}</div>
                      <div className="performer-deliveries">{performer.deliveries} deliveries</div>
                    </div>
                    <div className="performer-rating">
                      <Star size={16} fill="currentColor" className="star-icon" />
                      <span>{performer.rating}</span>
                    </div>
                  </div>
              ))}
            </div>
          </div>

          <div className="performance-metrics-section">
            <h2>Performance Metrics</h2>
            <div className="metrics-list">
              <div className="progress-item">
                <div className="progress-header">
                  <span className="progress-label" style={{ color: '#059669' }}>On-Time Deliveries</span>
                  <span className="progress-value" style={{ color: '#059669' }}>94.3%</span>
                </div>
                <div className="progress-bar-bg" style={{ backgroundColor: '#D1FAE5' }}>
                  <div className="progress-bar-fill" style={{ width: '94.3%', backgroundColor: '#10B981' }}></div>
                </div>
              </div>

              <div className="progress-item">
                <div className="progress-header">
                  <span className="progress-label" style={{ color: '#2563EB' }}>Customer Satisfaction</span>
                  <span className="progress-value" style={{ color: '#2563EB' }}>92.8%</span>
                </div>
                <div className="progress-bar-bg" style={{ backgroundColor: '#DBEAFE' }}>
                  <div className="progress-bar-fill" style={{ width: '92.8%', backgroundColor: '#3B82F6' }}></div>
                </div>
              </div>

              <div className="progress-item">
                <div className="progress-header">
                  <span className="progress-label" style={{ color: '#7C3AED' }}>Route Efficiency</span>
                  <span className="progress-value" style={{ color: '#7C3AED' }}>88.5%</span>
                </div>
                <div className="progress-bar-bg" style={{ backgroundColor: '#EDE9FE' }}>
                  <div className="progress-bar-fill" style={{ width: '88.5%', backgroundColor: '#8B5CF6' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}